import { requestJson } from "@/shared/services/api";
import type {
  PublishedArticleCard,
  PublishedArticleDetail,
} from "../types/content.types";
import type { Locale } from "@/shared/i18n/locale";

export interface PublicArticleCardResponse {
  is_featured?: boolean;
  id: string;
  area: string;
  reading_time: string;
  title: string;
  thumbnail: string;
  thumbnail_alt?: string;
  thumbnail_position_x?: number;
  thumbnail_position_y?: number;
  thumbnail_zoom?: number;
  href: string;
  slug: string;
  first_published_at: string | null;
}

export interface PublicArticleDetailResponse extends PublicArticleCardResponse {
  excerpt: string;
  body_markdown: string;
  seo_title: string;
  seo_description: string;
  author: string;
  author_image_url?: string | null;
  first_published_at: string | null;
  published_at: string | null;
  updated_at: string | null;
  translation_available?: boolean;
  original_href?: string | null;
}

export function toArticleCard(article: PublicArticleCardResponse): PublishedArticleCard {
  return {
    isFeatured: article.is_featured ?? false,
    id: article.id,
    area: article.area,
    readingTime: article.reading_time,
    title: article.title,
    thumbnail: article.thumbnail,
    thumbnailAlt: article.thumbnail_alt || article.title,
    thumbnailPosition: {
      x: article.thumbnail_position_x ?? 50,
      y: article.thumbnail_position_y ?? 50,
      zoom: article.thumbnail_zoom ?? 100,
    },
    href: article.href,
    slug: article.slug,
    firstPublishedAt: article.first_published_at,
  };
}

export function toArticleDetail(article: PublicArticleDetailResponse): PublishedArticleDetail {
  return {
    ...toArticleCard(article),
    excerpt: article.excerpt,
    bodyMarkdown: article.body_markdown,
    seoTitle: article.seo_title,
    seoDescription: article.seo_description,
    author: article.author,
    authorImageUrl: article.author_image_url ?? null,
    firstPublishedAt: article.first_published_at,
    publishedAt: article.published_at,
    updatedAt: article.updated_at,
    translationAvailable: article.translation_available,
    originalHref: article.original_href ?? null,
  };
}

export async function getPublishedArticles(
  locale?: Locale,
  signal?: AbortSignal,
): Promise<PublishedArticleCard[]> {
  const query = locale === "en" ? "?locale=en" : "";
  const articles = await requestJson<PublicArticleCardResponse[]>(
    `/api/public/articles${query}`,
    { signal, cache: "no-cache" },
  );
  return articles.map(toArticleCard);
}

export async function getPublishedArticleBySlug(
  slug: string,
  signal?: AbortSignal,
  locale?: Locale,
): Promise<PublishedArticleDetail> {
  const query = locale === "en" ? "?locale=en" : "";
  const article = await requestJson<PublicArticleDetailResponse>(
    `/api/public/articles/${slug}${query}`,
    { signal, cache: "no-cache" },
  );
  return toArticleDetail(article);
}
